/**
 * Types
 */
type SourceUrl = string;
type MediaId = string;

type Resource = {
  signedUrlExp: number;
  mediaIds: Set<MediaId>;
};

type VideoCache = Record<MediaId, Set<SourceUrl>>;
type SourceCache = Record<SourceUrl, Resource>;

export const sourceCache: SourceCache = {};
export const videoCache: VideoCache = {};

/**
 * Checks if a source URL has a signed URL expiration timestamp.
 * @param sourceUrl - The source URL to check.
 * @returns `true` if the URL is signed; otherwise, `false`.
 */
export const isSignedUrl = (sourceUrl: SourceUrl): boolean => !!sourceCache[sourceUrl]?.signedUrlExp;

/**
 * Retrieves all video resources associated with a source URL.
 * @param sourceUrl - The source URL to retrieve resources for.
 * @returns An array of objects containing `mediaId` and `sourceUrl`.
 */
export const getAllVideoResources = (sourceUrl: SourceUrl): Array<{ mediaId: MediaId; sourceUrl: SourceUrl }> => {
  if (!sourceCache[sourceUrl]) {
    return [];
  }
  const mediaIds = Array.from(sourceCache[sourceUrl].mediaIds);
  return mediaIds.reduce(
    (acc, mediaId) => acc.concat([...videoCache[mediaId]].map((sourceUrl$) => ({ mediaId, sourceUrl: sourceUrl$ }))),
    [] as Array<{ mediaId: MediaId; sourceUrl: SourceUrl }>,
  );
};

/**
 * Adds or updates a source URL in the cache with its expiration and associated media ID.
 * @param mediaId - The media ID associated with the source URL.
 * @param sourceUrl - The source URL to cache.
 * @param signedUrlExp - The signed URL expiration timestamp.
 */
export const setSourceCache = (mediaId: MediaId, sourceUrl: SourceUrl, signedUrlExp: number): void => {
  if (!sourceCache[sourceUrl]) {
    sourceCache[sourceUrl] = {
      signedUrlExp,
      mediaIds: new Set([mediaId]),
    };
  } else {
    sourceCache[sourceUrl].signedUrlExp = signedUrlExp;
    sourceCache[sourceUrl].mediaIds.add(mediaId);
  }

  if (videoCache[mediaId] && !videoCache[mediaId].has(sourceUrl)) {
    videoCache[mediaId].add(sourceUrl);
  } else if (!videoCache[mediaId]) {
    videoCache[mediaId] = new Set([sourceUrl]);
  }
};

/**
 * Updates the source URL in the cache with a new URL and expiration, removing the old URL.
 * @param mediaId - The media ID associated with the source URL.
 * @param oldSourceUrl - The old source URL to replace.
 * @param newSourceUrl - The new source URL.
 * @param signedUrlExp - The signed URL expiration timestamp for the new source URL.
 */
export const updateSourceCache = (
  mediaId: MediaId,
  oldSourceUrl: SourceUrl,
  newSourceUrl: SourceUrl,
  signedUrlExp: number,
): void => {
  delete sourceCache[oldSourceUrl];
  videoCache[mediaId].delete(oldSourceUrl);
  setSourceCache(mediaId, newSourceUrl, signedUrlExp);
};

/**
 * Source validation and resource update management
 */
let timer: number | undefined;
let sourcesToDispatch: Array<{ mediaId: MediaId; sourceUrl: SourceUrl }> = [];

/**
 * Validates the expiration of a source URL's signed URL and triggers an update if expired.
 * @param sourceUrl - The source URL to validate.
 * @param updateResourcesStartedAction - The action to dispatch with expired resources.
 */
export function validateSourceCache(
  sourceUrl: SourceUrl,
  updateResourcesStartedAction: (resources: { mediaId: string; sourceUrl: string }[]) => void,
): void {
  const signedUrlExp = sourceCache[sourceUrl]?.signedUrlExp;
  if (signedUrlExp) {
    if (signedUrlExp <= Math.floor(Date.now() / 1000)) {
      const resources = getAllVideoResources(sourceUrl);
      sourcesToDispatch = sourcesToDispatch.concat(resources);

      clearTimeout(timer!);
      timer = window.setTimeout(() => {
        updateResourcesStartedAction(sourcesToDispatch);
        sourcesToDispatch = [];
      }, 300);
    }
  }
}
